import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import bgImg from "../../Assets/Home/57307.jpg";
import axios from "axios";
import InquiryForm from "./Inquiryform";

function parseBullet(text) {
  const dashIdx = text.indexOf(" - ");
  if (dashIdx !== -1 && dashIdx < 40) {
    return { title: text.slice(0, dashIdx).trim(), body: text.slice(dashIdx + 3).trim() };
  }
  return { title: null, body: text };
}

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" className="flex-shrink-0 mt-0.5">
    <circle cx="10" cy="10" r="9" fill="#4ade80" opacity="0.15" />
    <path d="M6 10l3 3 5-6" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TagIcon = () => (
  <svg viewBox="0 0 20 20" width="13" height="13" fill="currentColor">
    <path d="M9.293 1.293a1 1 0 011.414 0l8 8a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-8-8A1 1 0 013 8V3a2 2 0 012-2h4.586zM7 6a1 1 0 100-2 1 1 0 000 2z" />
  </svg>
);

const BackIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
    <path d="M13 4l-6 6 6 6" />
  </svg>
);

const SectionHeader = ({ title }) => (
  <div className="flex items-center gap-2.5 mb-5">
    <div className="w-1 h-6 rounded-full flex-shrink-0"
      style={{ background: "linear-gradient(to bottom, #6ee85a, #3dcc40)" }} />
    <h2 className="font-black text-gray-900 text-base sm:text-lg">{title}</h2>
  </div>
);

export default function ServiceDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [activeImg, setActiveImg] = useState(null);
  const [showMobileForm, setShowMobileForm] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/product/${id}`);
      setService(res.data.product);
      setActiveImg(res.data.product.mainImage);
    } catch (error) {
      console.log("Error occurred", error);
    } finally {
      setLoading(false);
    }
  };

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#0d2d0d" }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full border-4 border-green-400 border-t-transparent animate-spin" />
          <p className="text-green-400 text-xs font-semibold tracking-widest uppercase">Loading...</p>
        </div>
      </div>
    );
  }

  if (!service) return null;

  const allImages = [service.mainImage, ...(service.smallImages || [])].filter(Boolean);
  const formattedDate = new Date(service.createdAt).toLocaleDateString("en-IN", {
    year: "numeric", month: "long", day: "numeric",
  });

  return (
    <div className="min-h-screen" style={{ background: "#f8faf6", fontFamily: "'Barlow', 'Montserrat', sans-serif" }}>

      {/* ══════════════════════════════
          HERO BANNER
      ══════════════════════════════ */}
      <div
        className="relative overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `url(${bgImg})`, minHeight: "200px" }}
      >
        <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.5)" }} />
        <div className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, rgba(74,222,128,0.06) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-300 mb-3 flex-wrap">
            <button onClick={() => navigate("/")} className="hover:text-green-400 transition-colors">Home</button>
            <span className="text-green-700">/</span>
            <button onClick={() => navigate("/services")} className="hover:text-green-400 transition-colors">Services</button>
            <span className="text-green-700">/</span>
            <span className="text-white font-semibold line-clamp-1">{service.heading}</span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
            <div className="flex-1 min-w-0">
              {service.category && (
                <span className="inline-flex items-center gap-1.5 text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-3"
                  style={{ background: "rgba(74,222,128,0.15)", color: "#4ade80", border: "1px solid rgba(74,222,128,0.3)" }}>
                  <TagIcon />{service.category.name}
                </span>
              )}
              <h1 className="text-white font-josefin my-3 font-bold leading-tight"
                style={{ fontSize: "clamp(18px, 4vw, 42px)", letterSpacing: "-0.02em" }}>
                {service.heading}
              </h1>
              <p className="text-green-400 text-xs mt-2 font-medium">Published: {formattedDate}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════
          MOBILE — FLOATING QUOTE BTN
      ══════════════════════════════ */}
      <div className="lg:hidden fixed bottom-5 right-4 z-50">
        <button
          onClick={() => setShowMobileForm(true)}
          className="font-black text-xs px-5 py-3 rounded-full shadow-2xl flex items-center gap-2 transition-all hover:scale-105"
          style={{
            background: "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)",
            color: "#0d2d0d",
            boxShadow: "0 6px 24px rgba(74,222,128,0.5)",
          }}
        >
          📋 Get Free Quote
        </button>
      </div>

      {/* ══════════════════════════════
          MOBILE — FORM BOTTOM SHEET
      ══════════════════════════════ */}
      {showMobileForm && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end"
          style={{ background: "rgba(0,0,0,0.6)" }}
          onClick={() => setShowMobileForm(false)}
        >
          <div
            className="rounded-t-3xl overflow-y-auto"
            style={{ maxHeight: "92vh", background: "#f8faf6" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full bg-gray-300" />
            </div>
            <div className="flex justify-end px-4 pt-1">
              <button onClick={() => setShowMobileForm(false)} className="text-gray-400 hover:text-gray-600 text-xl font-bold">✕</button>
            </div>
            <div className="px-4 pb-8">
              <InquiryForm serviceTitle={service.heading} />
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════
          MAIN CONTENT
      ══════════════════════════════ */}
      <div className=" px-4 sm:px-6 md:px-10 lg:px-12 xl:px-20 py-8 sm:py-12">

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 items-start">

          <div className="md:col-span-1 lg:col-span-3 flex flex-col gap-4 lg:sticky lg:top-6">

            {/* Main image */}
            <div className="relative rounded-2xl overflow-hidden"
              style={{
                background: "#e8f0e0",
                border: "2px solid rgba(74,222,128,0.2)",
                boxShadow: "0 6px 32px rgba(74,222,128,0.1)",
                aspectRatio: "1/1",
              }}
            >
              <img
                src={activeImg || service.mainImage}
                alt={service.heading}
                className="w-full h-full object-contain p-3 transition-opacity duration-300"
                style={{ opacity: imgLoaded ? 1 : 0 }}
                onLoad={() => setImgLoaded(true)}
              />
              {!imgLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-8 h-8 rounded-full border-2 border-green-400 border-t-transparent animate-spin" />
                </div>
              )}
              {service.isActive && (
                <div className="absolute top-3 left-3 flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ background: "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)", color: "#0d2d0d" }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                  Active
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {allImages.length > 1 && (
              <div className="flex gap-2 flex-wrap">
                {allImages.map((img, i) => (
                  <button key={i}
                    onClick={() => { setActiveImg(img); setImgLoaded(false); }}
                    className="rounded-xl overflow-hidden transition-all duration-200"
                    style={{
                      width: "52px", height: "52px",
                      border: activeImg === img ? "2px solid #4ade80" : "2px solid #e0ead8",
                      background: "#e8f0e0",
                    }}
                  >
                    <img src={img} alt="" className="w-full h-full object-contain p-1" />
                  </button>
                ))}
              </div>
            )}

            {/* Quick specs — hidden on mobile */}
            <div className="hidden sm:block rounded-2xl p-5"
              style={{ background: "#0d2d0d", border: "1px solid rgba(74,222,128,0.2)" }}>
              <p className="text-green-400 text-xs font-bold tracking-widest uppercase mb-4">Quick Specs</p>
              <div className="space-y-3">
                {service.bulletDescription.slice(0, 3).map((b, i) => {
                  const { title, body } = parseBullet(b);
                  if (!title) return null;
                  return (
                    <div key={i} className="flex items-start justify-between gap-2">
                      <span className="text-gray-400 text-xs flex-shrink-0">{title}</span>
                      <span className="text-white text-xs font-semibold bg-green-900/30 px-2 py-0.5 rounded-full text-right">{body}</span>
                    </div>
                  );
                })}
              </div>
              <button
                className="w-full mt-5 py-2.5 rounded-xl font-black text-sm tracking-wide transition-all hover:shadow-lg hover:scale-105"
                style={{ background: "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)", color: "#0d2d0d" }}
              >
                Request a Quote
              </button>
              <button
                className="w-full mt-2.5 py-2.5 rounded-xl font-semibold text-sm"
                style={{ background: "transparent", color: "#4ade80", border: "1.5px solid rgba(74,222,128,0.4)" }}
              >
                Contact Us
              </button>
            </div>
          </div>

          {/* ── CONTENT COLUMN ───────────────────
              mobile:  full width
              tablet:  col 2 of 2
              desktop: col-span-6 of 12
          ──────────────────────────────────── */}
          <div className="md:col-span-1 lg:col-span-6 flex flex-col gap-5 sm:gap-6">

            {/* Specifications */}
            {service.bulletDescription.length > 0 && (
              <div className="rounded-2xl p-5 sm:p-7"
                style={{ background: "white", border: "1px solid #e8f0e0", boxShadow: "0 2px 16px rgba(0,0,0,0.04)" }}>
                <SectionHeader title="Specifications & Details" />
                <div className="space-y-2.5">
                  {service.bulletDescription.map((b, i) => {
                    const { title, body } = parseBullet(b);
                    return (
                      <div key={i} className="flex items-start gap-3 p-3 sm:p-4 rounded-xl transition-colors hover:bg-green-50"
                        style={{ border: "1px solid #f0f5ee" }}>
                        <CheckIcon />
                        <div className="text-sm leading-relaxed">
                          {title && <span className="font-bold text-gray-900 mr-1">{title} —</span>}
                          <span className="text-gray-600">{body}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Overview */}
            <div className="rounded-2xl p-5 sm:p-7"
              style={{ background: "white", border: "1px solid #e8f0e0", boxShadow: "0 2px 16px rgba(0,0,0,0.04)" }}>
              <SectionHeader title="Overview" />
              <div className="text-gray-600 text-sm leading-relaxed" style={{ lineHeight: 1.85 }}
                dangerouslySetInnerHTML={{ __html: service.description }} />
            </div>

            {/* Additional Points */}
            {service.additionalPoints?.length > 0 && (
              <div className="rounded-2xl p-5 sm:p-7"
                style={{ background: "white", border: "1px solid #e8f0e0" }}>
                <SectionHeader title="Additional Information" />
                <div className="space-y-3">
                  {service.additionalPoints.map((pt, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckIcon />
                      <p className="text-gray-600 text-sm leading-relaxed">{pt}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* More Product Info */}
            {service.moreProductInfo?.length > 0 && (
              <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid #e8f0e0" }}>
                <div className="px-5 sm:px-7 py-4" style={{ background: "#0d2d0d" }}>
                  <h2 className="font-black text-white text-base sm:text-lg">Product Details</h2>
                </div>
                <div className="bg-white divide-y divide-[#f0f5ee]">
                  {service.moreProductInfo.map((info, i) => (
                    <div key={i} className="flex items-start gap-4 px-5 sm:px-7 py-3">
                      <span className="text-gray-500 text-sm w-2/5 sm:w-1/3 font-medium flex-shrink-0">{info.key}</span>
                      <span className="text-gray-800 text-sm font-semibold">{info.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category chip */}
            {service.category && (
              <div className="flex items-center gap-3 flex-wrap pb-2">
                <span className="text-gray-500 text-xs font-semibold uppercase tracking-widest">Category:</span>
                <button
                  onClick={() => navigate(`/services/category/${service.category.slug}`)}
                  className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all hover:opacity-80"
                  style={{ background: "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)", color: "#0d2d0d" }}
                >
                  <TagIcon />{service.category.name}
                </button>
              </div>
            )}
          </div>

          {/* Tablet: form below everything, full width */}
          <div className="md:col-span-2 lg:hidden">
            <InquiryForm serviceTitle={service.heading} />
          </div>

          {/* Desktop: sticky right column */}
          <div className="hidden lg:block lg:col-span-3 sticky top-6">
            <InquiryForm serviceTitle={service.heading} />
          </div>

        </div>
      </div>
    </div>
  );
}


// import { useEffect, useState } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import bgImg from '../../Assets/Home/57307.jpg'
// import axios from "axios";
// import InquiryForm from "./Inquiryform";

// // ── Mock API fetch — replace with your real API call ──
// async function fetchService(id) {
//   // Replace this with: const res = await fetch(`/api/services/${slug}`);
//   return {
//     _id: "69c22f136443ab567ea108fc",
//     heading: "Solar Panel Mounting Structure",
//     slug: "solar-panel-mounting-structure",
//     description:
//       "<h3>A solar panel mounting structure, also known as a racking system or mounting system, is a crucial component of a <strong>solar photovoltaic (PV) system</strong>. Its primary function is to securely support solar panels on various types of surfaces, such as rooftops, ground mounts, or pole mounts, while ensuring optimal orientation and tilt for maximum solar energy production. Here are the key aspects and considerations related to solar panel mounting structures:</h3>",
//     mainImage:
//       "https://res.cloudinary.com/dlfg91ln5/raw/upload/v1774333583/solar-panel-mounting-structure-500x500_ozxxyg.webp",
//     smallImages: [],
//     bulletDescription: [
//       "Design Type - Modular",
//       "Frame Material - Aluminium",
//       "Size - 3.8 x 6 x 3 Feet",
//       "Types of Mounting Structures:  Roof Mounts: These are designed to attach solar panels directly to the roof of a building. They can be installed on various roof types, including pitched roofs (tile, asphalt shingle, metal) and flat roofs. Ground Mounts: Ground-mounted systems are installed on the ground using frames or racks. They are suitable for installations where roof space is limited or where specific orientation and tilt angles are required for optimal solar energy production. Pole Mounts: These are used for mounting solar panels on single or multiple poles, often in remote locations or areas where ground space is limited",
//     ],
//     additionalPoints: [],
//     category: {
//       _id: "69b8edeec18d1218a43abf5a",
//       name: "Solar Panel Mounting Structure",
//       slug: "solar-panel-mounting-structure",
//     },
//     moreProductInfo: [],
//     isActive: true,
//     createdAt: "2026-03-24T06:28:35.897Z",
//   };
// }

// // ── Parse bullet text into title + body if it has a dash
// function parseBullet(text) {
//   const dashIdx = text.indexOf(" - ");
//   if (dashIdx !== -1 && dashIdx < 40) {
//     return { title: text.slice(0, dashIdx).trim(), body: text.slice(dashIdx + 3).trim() };
//   }
//   return { title: null, body: text };
// }

// // ── Check icon ──
// const CheckIcon = () => (
//   <svg viewBox="0 0 20 20" width="16" height="16" fill="none">
//     <circle cx="10" cy="10" r="9" fill="#4ade80" opacity="0.15" />
//     <path d="M6 10l3 3 5-6" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
//   </svg>
// );

// // ── Tag icon ──
// const TagIcon = () => (
//   <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
//     <path d="M9.293 1.293a1 1 0 011.414 0l8 8a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-8-8A1 1 0 013 8V3a2 2 0 012-2h4.586zM7 6a1 1 0 100-2 1 1 0 000 2z" />
//   </svg>
// );

// export default function ServiceDetails() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const [service, setService] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [imgLoaded, setImgLoaded] = useState(false);
//   const [activeImg, setActiveImg] = useState(null);

//   useEffect(() => {
//     fetchProduct();
//   }, [id]);

//   const fetchProduct  = async()=>{

//     try {
//         setLoading(true);
//         const res = await axios.get(`${process.env.REACT_APP_API_URL}/product/${id}`);
//         setService(res.data.product);
//         setActiveImg(res.data.product.mainImage);
      
//     } catch (error) {
//         console.log("Error is occured", error);
//     }finally{
//         setLoading(false);
//     }
//   }

//   if (loading) {
//     return (
//       <div
//         className="min-h-screen flex items-center justify-center"
//         style={{ background: "#0d2d0d" }}
//       >
//         <div className="flex flex-col items-center gap-4">
//           <div
//             className="w-14 h-14 rounded-full border-4 border-green-400 border-t-transparent animate-spin"
//           />
//           <p className="text-green-400 text-sm font-semibold tracking-widest uppercase">Loading...</p>
//         </div>
//       </div>
//     );
//   }

//   if (!service) return null;

//   const allImages = [service.mainImage, ...(service.smallImages || [])].filter(Boolean);
//   const formattedDate = new Date(service.createdAt).toLocaleDateString("en-IN", {
//     year: "numeric", month: "long", day: "numeric",
//   });

//   return (
//     <div
//       className="min-h-screen"
//       style={{
//         background: "#f8faf6",
//         fontFamily: "'Barlow', 'Montserrat', sans-serif",
//       }}
//     >
//       {/* ── Hero banner ── */}
//       <div
//         className="relative overflow-hidden bg-cover bg-center"
//         style={{
//           backgroundImage: `url(${bgImg})`,
//           minHeight: "280px",
//         }}
//       >
//         {/* Dot texture */}
//         <div
//           className="absolute inset-0 pointer-events-none"
//           style={{
//             backgroundImage: "radial-gradient(circle, rgba(74,222,128,0.06) 1px, transparent 1px)",
//             backgroundSize: "24px 24px",
//           }}
//         />

//         <div className="relative z-10 max-w-7xl mx-auto px-8 py-14">
//           {/* Breadcrumb */}
//           <div className="flex items-center gap-2 text-sm text-gray-700 mb-4 flex-wrap">
//             <button onClick={() => navigate("/")} className="hover:text-white transition-colors">Home</button>
//             <span className="text-green-700">/</span>
//             <button onClick={() => navigate("/services")} className="hover:text-white transition-colors">Services</button>
//             <span className="text-green-700">/</span>
//             <span className="text-white font-semibold">{service.heading}</span>
//           </div>

//           <div className="flex items-start justify-between flex-wrap gap-4">
//             <div>
//               {/* Category badge */}
//               {service.category && (
//                 <div className="flex items-center gap-1.5 mb-3">
//                   <span
//                     className="flex items-center gap-1.5 text-xs bg-green-300 text-gray-600 font-bold tracking-widest uppercase px-3 py-1 rounded-full"
//                     style={{ border: "1px solid rgba(74,222,128,0.3)" }}
//                   >
//                     <TagIcon />
//                     {service.category.name}
//                   </span>
//                 </div>
//               )}
//               <h1
//                 className="text-gray-800 font-bold leading-relaxed"
//                 style={{ fontSize: "clamp(24px, 4vw, 48px)", letterSpacing: "-0.02em" }}
//               >
//                 {service.heading}
//               </h1>
//               <p className="text-green-900 text-xs mt-2 font-medium">Published: {formattedDate}</p>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* ── Main content ── */}
//       <div className="px-6 md:px-10 lg:px-12 xl:px-20 py-12">
//         <div className="grid grid-cols-1 lg:grid-cols-4 gap-10 items-start">

//           {/* ── Left: Image gallery ── */}
//           <div className="lg:col-span-1 flex flex-col gap-4 sticky top-8">
//             {/* Main image */}
//             <div
//               className="relative rounded-3xl overflow-hidden"
//               style={{
//                 background: "#e8f0e0",
//                 border: "2px solid rgba(74,222,128,0.2)",
//                 boxShadow: "0 8px 40px rgba(74,222,128,0.12)",
//                 aspectRatio: "1/1",
//               }}
//             >
//               <img
//                 src={activeImg || service.mainImage}
//                 alt={service.heading}
//                 className="w-full h-full object-contain p-4 transition-opacity duration-300"
//                 style={{ opacity: imgLoaded ? 1 : 0 }}
//                 onLoad={() => setImgLoaded(true)}
//               />
//               {!imgLoaded && (
//                 <div className="absolute inset-0 flex items-center justify-center">
//                   <div className="w-8 h-8 rounded-full border-2 border-green-400 border-t-transparent animate-spin" />
//                 </div>
//               )}

//               {/* Active badge */}
//               {service.isActive && (
//                 <div
//                   className="absolute top-4 left-4 flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full"
//                   style={{ background: "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)", color: "#0d2d0d" }}
//                 >
//                   <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
//                   Active
//                 </div>
//               )}
//             </div>

//             {/* Thumbnail strip */}
//             {allImages.length > 1 && (
//               <div className="flex gap-3 flex-wrap">
//                 {allImages.map((img, i) => (
//                   <button
//                     key={i}
//                     onClick={() => { setActiveImg(img); setImgLoaded(false); }}
//                     className="rounded-xl overflow-hidden transition-all duration-200"
//                     style={{
//                       width: "64px", height: "64px",
//                       border: activeImg === img ? "2px solid #4ade80" : "2px solid transparent",
//                       background: "#e8f0e0",
//                       boxShadow: activeImg === img ? "0 0 12px rgba(74,222,128,0.4)" : "none",
//                     }}
//                   >
//                     <img src={img} alt="" className="w-full h-full object-contain p-1" />
//                   </button>
//                 ))}
//               </div>
//             )}

//             {/* Quick specs card */}
//             <div
//               className="rounded-2xl p-5"
//               style={{ background: "#89ea5f", border: "1px solid rgba(74,222,128,0.2)" }}
//             >
//               <p className="text-green-400 text-xs font-bold tracking-widest uppercase mb-4">Quick Specs</p>
//               <div className="space-y-3">
//                 {service.bulletDescription.slice(0, 3).map((b, i) => {
//                   const { title, body } = parseBullet(b);
//                   if (!title) return null;
//                   return (
//                     <div key={i} className="flex items-center justify-between">
//                       <span className="text-gray-400 text-xs">{title}</span>
//                       <span className="text-white text-xs font-semibold bg-green-900/30 px-2 py-0.5 rounded-full">{body}</span>
//                     </div>
//                   );
//                 })}
//               </div>

//               {/* CTA */}
//               <button
//                 className="w-full mt-5 py-3 rounded-xl font-black text-sm tracking-wide transition-all duration-200 hover:shadow-lg hover:scale-105"
//                 style={{
//                   background: "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)",
//                   color: "#0d2d0d",
//                   boxShadow: "0 4px 16px rgba(74,222,128,0.3)",
//                 }}
//               >
//                 Request a Quote
//               </button>
//               <button
//                 className="w-full mt-3 py-3 rounded-xl font-semibold text-sm tracking-wide transition-all duration-200"
//                 style={{
//                   background: "transparent",
//                   color: "#4ade80",
//                   border: "1.5px solid rgba(74,222,128,0.4)",
//                 }}
//               >
//                 Contact Us
//               </button>
//             </div>
//           </div>

//           {/* ── Right: Content ── */}
//           <div className="lg:col-span-2 flex flex-col gap-8">

//             {/* Description */}
//             <div
//               className="rounded-3xl p-8"
//               style={{ background: "white", border: "1px solid #e8f0e0", boxShadow: "0 2px 20px rgba(0,0,0,0.04)" }}
//             >
//               <div className="flex items-center gap-2 mb-5">
//                 <div
//                   className="w-1 h-6 rounded-full"
//                   style={{ background: "linear-gradient(to bottom, #6ee85a, #3dcc40)" }}
//                 />
//                 <h2 className="font-black text-gray-900 text-lg">Overview</h2>
//               </div>
//               <div
//                 className="text-gray-600 text-sm leading-relaxed prose-sm"
//                 style={{ lineHeight: 1.8 }}
//                 dangerouslySetInnerHTML={{ __html: service.description }}
//               />
//             </div>

//             {/* Bullet descriptions */}
//             {service.bulletDescription.length > 0 && (
//               <div
//                 className="rounded-3xl p-8"
//                 style={{ background: "white", border: "1px solid #e8f0e0", boxShadow: "0 2px 20px rgba(0,0,0,0.04)" }}
//               >
//                 <div className="flex items-center gap-2 mb-6">
//                   <div
//                     className="w-1 h-6 rounded-full"
//                     style={{ background: "linear-gradient(to bottom, #6ee85a, #3dcc40)" }}
//                   />
//                   <h2 className="font-black text-gray-900 text-lg">Specifications & Details</h2>
//                 </div>

//                 <div className="space-y-4">
//                   {service.bulletDescription.map((b, i) => {
//                     const { title, body } = parseBullet(b);
//                     return (
//                       <div
//                         key={i}
//                         className="flex items-start gap-3 p-4 rounded-2xl transition-colors duration-200 hover:bg-green-50"
//                         style={{ border: "1px solid #f0f5ee" }}
//                       >
//                         <div className="flex-shrink-0 mt-0.5">
//                           <CheckIcon />
//                         </div>
//                         <div>
//                           {title && (
//                             <span className="font-bold text-gray-900 text-sm mr-2">{title} —</span>
//                           )}
//                           <span className="text-gray-600 text-sm leading-relaxed">{body}</span>
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>
//             )}

//             {/* Additional Points */}
//             {service.additionalPoints?.length > 0 && (
//               <div
//                 className="rounded-3xl p-8"
//                 style={{ background: "white", border: "1px solid #e8f0e0" }}
//               >
//                 <div className="flex items-center gap-2 mb-6">
//                   <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(to bottom, #6ee85a, #3dcc40)" }} />
//                   <h2 className="font-black text-gray-900 text-lg">Additional Information</h2>
//                 </div>
//                 <div className="space-y-3">
//                   {service.additionalPoints.map((pt, i) => (
//                     <div key={i} className="flex items-start gap-3">
//                       <CheckIcon />
//                       <p className="text-gray-600 text-sm leading-relaxed">{pt}</p>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {/* More Product Info */}
//             {service.moreProductInfo?.length > 0 && (
//               <div
//                 className="rounded-3xl overflow-hidden"
//                 style={{ border: "1px solid #e8f0e0" }}
//               >
//                 <div
//                   className="px-8 py-5"
//                   style={{ background: "#0d2d0d" }}
//                 >
//                   <h2 className="font-black text-white text-lg">Product Details</h2>
//                 </div>
//                 <div className="bg-white">
//                   {service.moreProductInfo.map((info, i) => (
//                     <div
//                       key={i}
//                       className="flex items-start gap-4 px-8 py-4 border-b last:border-0"
//                       style={{ borderColor: "#f0f5ee" }}
//                     >
//                       <span className="text-gray-500 text-sm w-1/3 font-medium">{info.key}</span>
//                       <span className="text-gray-800 text-sm font-semibold">{info.value}</span>
//                     </div>
//                   ))}
//                 </div>
//               </div>
//             )}

//             {/* Category chip */}
//             {service.category && (
//               <div className="flex items-center gap-3 flex-wrap">
//                 <span className="text-gray-500 text-xs font-semibold uppercase tracking-widest">Category:</span>
//                 <button
//                   onClick={() => navigate(`/services/category/${service.category.slug}`)}
//                   className="flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full transition-all duration-200 hover:bg-green-600"
//                   style={{
//                     background: "linear-gradient(135deg, #6ee85a 0%, #3dcc40 100%)",
//                     color: "#0d2d0d",
//                     boxShadow: "0 2px 8px rgba(74,222,128,0.3)",
//                   }}
//                 >
//                   <TagIcon />
//                   {service.category.name}
//                 </button>
//               </div>
//             )}
//           </div>

//             <div className="lg:col-span-1">
//                 <InquiryForm serviceTitle={service.heading} />
//             </div>
//         </div>
//       </div>
//     </div>
//   );
// }